/**
 * SectionValidationBadge - displays validation status for song sections
 *
 * Shows a compact badge with:
 * - Check (✓) icon for valid sections
 * - Warning (!) icon for sections with issues
 * - Tooltip with detailed validation messages
 * - Color-coded status (green for valid, orange/red for warnings/errors)
 */

import { useState } from 'react'
import type { ValidationResult } from '../../../utils/sectionValidator'
import { getValidationSummary } from '../../../utils/sectionValidator'

interface SectionValidationBadgeProps {
  validation: ValidationResult
  className?: string
}

/**
 * SectionValidationBadge component
 * Renders a small badge showing validation status with tooltip
 *
 * Note: Tooltip positioning is handled by CSS using position: fixed
 * to avoid direct style manipulation (Lexical anti-pattern)
 */
export function SectionValidationBadge({
  validation,
  className = '',
}: SectionValidationBadgeProps) {
  const [showTooltip, setShowTooltip] = useState(false)

  const hasErrors = validation.issues.some((i) => i.type === 'error')
  const hasWarnings = validation.issues.some((i) => i.type === 'warning')

  // Determine badge color/icon based on validation state
  const badgeClass = hasErrors
    ? 'section-validation-badge--error'
    : hasWarnings
      ? 'section-validation-badge--warning'
      : 'section-validation-badge--valid'

  const icon = validation.isValid && !hasWarnings ? '✓' : '!'

  const summary = getValidationSummary(validation)

  return (
    <span className="section-validation-badge-wrapper">
      <span
        className={`section-validation-badge ${badgeClass} ${className}`}
        data-testid="section-validation-badge"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        aria-label={`Section validation: ${summary}`}
        role="status"
      >
        {icon}
      </span>

      {showTooltip && (
        <div
          className="section-validation-tooltip"
          data-testid="section-validation-tooltip"
          role="tooltip"
        >
          {summary}
        </div>
      )}
    </span>
  )
}
